import { ChatNotificationLevelEnum } from './chat-channel-member.model';
import { ChatChannelTypeEnum } from './chat-channel.model';
import { ChatFavoriteSection } from './chat-favorite-section.model';
import { ChatMessageReaction } from './chat-message.model';
import { ChatAvailabilityEnum } from './chat-user-setting.model';

// Internal Chat socket contract (SPEC-2026-07-internal-chat, T-16).
// Event names and payload shapes mirror the backend fan-out:
// - trendmulti-apps/apps/trendmulti-chat/src/services/delivery.service.ts
//   (chat:ack, chat:message:new/updated/deleted, chat:unread:updated)
// - channel.service.ts (chat:channel:updated, chat:pin:updated)
// - reaction.service.ts (chat:reaction:updated)
// - presence.service.ts (chat:presence:updated)
// - settings.service.ts (chat:status:updated)
// - trendmulti-websocket websocket.events.gateway.ts (chat:typing, chat:error)

/** Socket events EMITTED BY the backend (client listens). */
export const CHAT_SOCKET_EVENTS = {
  ack: 'chat:ack',
  messageNew: 'chat:message:new',
  messageUpdated: 'chat:message:updated',
  messageDeleted: 'chat:message:deleted',
  reactionUpdated: 'chat:reaction:updated',
  unreadUpdated: 'chat:unread:updated',
  readUpdated: 'chat:read:updated',
  presenceUpdated: 'chat:presence:updated',
  statusUpdated: 'chat:status:updated',
  channelUpdated: 'chat:channel:updated',
  pinUpdated: 'chat:pin:updated',
  typing: 'chat:typing',
  error: 'chat:error',
  // SPEC-2026-09-internal-chat-pwa — personal-room signals.
  surfaceUpdated: 'chat:surface:updated',
  sessionExpired: 'chat:session:expired',
  sessionRevoked: 'chat:session:revoked',
  favoriteUpdated: 'chat:favorite:updated',
  savedUpdated: 'chat:saved:updated',
  settingsUpdated: 'chat:settings:updated',
} as const;

/** Socket events EMITTED BY the client (gateway listens). */
export const CHAT_COMMAND_EVENT = 'chat:command';
export const CHAT_TYPING_EVENT = 'chat:typing';

// Chat room naming (plan §3.3) — joins are validated server-side; refusals
// emit `chat:error` { errorKey, room } and never join.
export const buildChatChannelRoom = (companyCode: string, channelId: string): string =>
  `chat_channel_${companyCode}_${channelId}`;
export const buildChatUserRoom = (companyCode: string, userId: string): string =>
  `chat_user_${companyCode}_${userId}`;
export const buildChatPresenceRoom = (companyCode: string): string =>
  `chat_presence_${companyCode}`;

/** `chat:ack` — correlated back to the pending command via requestId (RF-33). */
export interface ChatAckData {
  requestId: string;
  status: 'success' | 'error';
  /** i18n key resolved by the frontend catalogs */
  errorKey?: string;
}

/**
 * Synthetic errorKey used when no `chat:ack` arrives within the ack timeout —
 * generated client-side, never sent by the backend.
 */
export const CHAT_ACK_TIMEOUT_ERROR_KEY = 'chat.errors.ackTimeout';

/**
 * Synthetic errorKey for a command refused because the socket is down and the
 * caller opted out of the offline queue (`queueWhenOffline: false`). Also
 * client-side only.
 */
export const CHAT_OFFLINE_ERROR_KEY = 'chat.errors.offline';

/** `chat:message:deleted` — clients tombstone the message by id (soft delete, RF-10). */
export interface ChatMessageDeletedData {
  messageId: string;
  channelId: string;
  removed: boolean;
  /**
   * Set when the deleted message was the conversation's LAST one: the new
   * `lastMessagePreview` the server persisted (`[deleted]` token) — clients
   * retint the list row without refetching.
   */
  preview?: string;
}

/**
 * `chat:unread:updated` — two producers share this event: the new-message
 * fan-out (channelType/mentioned/preview/messageId/senderId present) and the
 * READ / MARK_UNREAD counter reset (manualUnread present).
 */
export interface ChatUnreadUpdatedData {
  channelId: string;
  count: number;
  channelType?: ChatChannelTypeEnum;
  mentioned?: boolean;
  preview?: string;
  messageId?: string;
  senderId?: string;
  manualUnread?: boolean;
}

/**
 * `chat:read:updated` — DIRECT conversations only (RF-33/AC-29). Broadcast to
 * the channel room when a member reads the conversation, so the other side can
 * mark its own messages up to `lastReadAt` as read. Never emitted for channels
 * or groups (per-message receipts at channel scale are a deferred v2 candidate).
 */
export interface ChatReadUpdatedData {
  channelId: string;
  /** Who read — the receiver ignores its own id. */
  userId: string;
  lastReadAt: Date;
}

/** `chat:reaction:updated` — full reactions array after the toggle. */
export interface ChatReactionUpdatedData {
  messageId: string;
  channelId: string;
  emoji: string;
  userId: string;
  added: boolean;
  reactions: Array<ChatMessageReaction>;
}

/**
 * `chat:surface:updated` — which of the user's surfaces have a live connection. Sent
 * on per-surface 0↔1 transitions and as a snapshot right after the personal room is
 * joined (SPEC-2026-09-internal-chat-pwa, ADR-004).
 */
export interface ChatSurfaceUpdatedData {
  webChatConnected: boolean;
  webAppConnected: boolean;
}

/**
 * `chat:session:expired` / `chat:session:revoked` — the gateway is about to close this
 * socket because its session ended (token expiry, or the product session it was
 * derived from signed out). `sid` is set on revocation.
 */
export interface ChatSessionEndedData {
  errorKey: string;
  sid?: string;
}

/** `chat:favorite:updated` — the user's favourite sections after a change on any surface. */
export interface ChatFavoriteUpdatedData {
  favoriteSections: ChatFavoriteSection[];
}

/** `chat:saved:updated` — a message saved or unsaved on any surface. */
export interface ChatSavedUpdatedData {
  messageId: string;
  channelId: string;
  saved: boolean;
}

/** `chat:settings:updated` — a notification level or the preferences changed on any surface. */
export interface ChatSettingsUpdatedData {
  notificationLevel?: { channelId: string; level: ChatNotificationLevelEnum };
  preferences?: {
    quietHoursEnabled: boolean;
    quietHoursStart: string | null;
    quietHoursEnd: string | null;
    soundEnabled: boolean;
  };
}

/** `chat:presence:updated` — broadcast to the company presence room. */
export interface ChatPresenceUpdatedData {
  userId: string;
  online: boolean;
}

/** `chat:status:updated` — availability/custom status broadcast. */
export interface ChatStatusUpdatedData {
  userId: string;
  availability: ChatAvailabilityEnum;
  statusEmoji: string | null;
  statusText: string | null;
  /** ISO date string */
  statusExpiresAt: string | null;
}

export type ChatChannelUpdateActionType = 'created' | 'joined' | 'left' | 'invited' | 'archived' | 'deleted';

/** `chat:channel:updated` — lifecycle fan-out from the channel service. */
export interface ChatChannelUpdatedData {
  channelId: string;
  action: ChatChannelUpdateActionType;
  actorId?: string;
  type?: ChatChannelTypeEnum;
  name?: string;
  memberIds?: string[];
}

/** `chat:pin:updated` — pin/unpin fan-out to the channel room. */
export interface ChatPinUpdatedData {
  channelId: string;
  messageId: string;
  action: 'pinned' | 'unpinned';
  actorId: string;
}

/** `chat:typing` — ephemeral relay; sender id stamped from the verified identity. */
export interface ChatTypingData {
  channelId: string;
  userId: string;
  userName?: string;
  typing: boolean;
}

/** `chat:error` — join refusals ({errorKey, room}) and command refusals ({errorKey, requestId}). */
export interface ChatErrorData {
  errorKey: string;
  room?: string;
  requestId?: string;
}
